/* eslint-disable react/prop-types */
import { Token } from '@kodiak-finance/sdk-core'
import RewardTokenSelector from 'components/KodiakFarms/RewardSelector'
import QuestionHelper from 'components/QuestionHelper'
import Slider from 'components/Slider'
import { memo, useEffect, useRef } from 'react'
import { AlertTriangle } from 'react-feather'

import {
  ErrorMessage,
  InputLabel,
  InputWrapper,
  SliderContainer,
  SliderValueDisplay,
  StyledInput,
  SubText,
} from './styled'

interface ConfigField {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  info?: string
  placeholder?: string
  isRewardToken?: boolean
  allTokens?: { [address: string]: Token }
  currentValue?: string
}

interface ConfigFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  info?: string
  placeholder?: string
  isRewardToken?: boolean
  sliderSelector?: {
    min?: number
    max?: number
    step?: number
    size?: number
    value?: string
  }
  isDateSelector?: boolean
  isSlider?: boolean
  allTokens?: { [address: string]: Token }
  currentValue?: string
  disabled?: boolean | null
  subText?: string
}

const ConfigField: React.FC<ConfigFieldProps> = ({
  label,
  value,
  onChange,
  error,
  info,
  placeholder,
  allTokens,
  isRewardToken,
  isDateSelector,
  isSlider,
  sliderSelector,
  currentValue,
  disabled,
  subText,
}) => {
  const initialValueSet = useRef(false)

  useEffect(() => {
    if (isRewardToken && currentValue && allTokens && !initialValueSet.current) {
      onChange(currentValue)
      initialValueSet.current = true
    }
  }, [currentValue, allTokens, isRewardToken, onChange])

  if (isRewardToken && allTokens) {
    return (
      <InputWrapper>
        <InputLabel>
          {label}
          {info && <QuestionHelper text={info} />}
        </InputLabel>
        <RewardTokenSelector
          value={value}
          onChange={(newValue) => {
            if (newValue !== value) {
              onChange(newValue)
            }
          }}
          placeholder="Select reward token"
          customPlaceholder="Enter reward token address (0x...)"
          allTokens={allTokens}
          disabled={disabled ?? null}
        />
        {error && (
          <ErrorMessage>
            <AlertTriangle size={16} />
            {error}
          </ErrorMessage>
        )}
      </InputWrapper>
    )
  }

  if (isSlider) {
    // const sliderValue = parseFloat(value) || 1
    return (
      <InputWrapper>
        <InputLabel>
          {label}
          {info && <QuestionHelper text={info} />}
        </InputLabel>
        <SliderContainer>
          <SliderValueDisplay>{sliderSelector?.value}</SliderValueDisplay>
          <Slider
            value={parseFloat(value)}
            onChange={(newValue) => onChange(newValue.toString())}
            min={sliderSelector?.min}
            max={sliderSelector?.max}
            step={sliderSelector?.step}
            size={sliderSelector?.size}
          />
        </SliderContainer>
        {error && (
          <ErrorMessage>
            <AlertTriangle size={16} />
            {error}
          </ErrorMessage>
        )}
      </InputWrapper>
    )
  }

  return (
    <InputWrapper>
      <InputLabel>
        {label}
        {info && <QuestionHelper text={info} />}
      </InputLabel>
      <StyledInput
        value={value}
        onChange={(e) => {
          if (e.target.value !== value) {
            onChange(e.target.value)
          }
        }}
        placeholder={placeholder}
        hasError={!!error}
      />
      {subText && <SubText>{`≅ ${parseFloat(subText).toFixed(8)}/sec`}</SubText>}
      {error && (
        <ErrorMessage>
          <AlertTriangle size={16} />
          {error}
        </ErrorMessage>
      )}
    </InputWrapper>
  )
}

export default memo(ConfigField)
